import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ScientificAnalysisDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource'
import { scientificAnalysisService } from '@/modules/scientific-analysis-module/services/scientific-analysis-services'
import { Nullable } from '@/shared/types/app.types'
import { ColDef } from '@ag-grid-enterprise/all-modules'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { ScientificAnalysisPatientDatasource } from '@/modules/scientific-analysis-module/components/scientific-analysis-results-panel/datasources/scientific-analysis-datasource-patient'
import { ScientificAnalysisEntityRequest } from '@/modules/scientific-analysis-module/services/scientific-analysis-dto-request'
import { store } from '@/store/store'
import buildIconHeaderTemplate from '@/grid/buildIconHeaderTemplate'

export class ScientificAnalysisRequestDatasource extends ScientificAnalysisDatasource<ScientificAnalysisEntityRequest> {
    public static getEntityGridColumns(selector: string = '', hideAll: boolean = false): (ColDef | ExportedColDef | ExportedColGroupDef) {
        return {
            headerName: i18n.tc(`scientificAnalysis.grid.results.request.header`),
            children: [
                {
                    type: AgGridColumnType.TEXT,
                    headerValueGetter: () => store.getters.parameters.global?.mainPatientIdentifierType.name,
                    filterParams: {
                        filterOptions: ['equals'],
                        defaultOption: 'equals',
                        newRowsAction: 'keep'
                    },
                    field: `${selector}caseStudy.patient.patientIdentifier.paiIdentifier`,
                    exportField: `${selector}caseStudy.patient.patientIdentifier.paiIdentifier`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.patientCenterIdentifier'),
                    exportField: `${selector}caseStudy.patient.patientCenterIdentifier.paiIdentifier`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}caseStudy.patient.patientCenterIdentifier.paiIdentifier`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.requestNumber'),
                    field: `${selector}requestNumber`,
                    exportField: `${selector}requestNumber`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.COMMON_REQUEST_STATUS,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.status`),
                    exportField: `${selector}status.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}status`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.physicianHealthManagementArea'),
                    field: `${selector}physicianHealthManagementArea.name`,
                    exportField: `${selector}physicianHealthManagementArea.name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.requestingCenter'),
                    field: `${selector}physicianDepartment.center.name`,
                    exportField: `${selector}physicianDepartment.center.name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.requestingProfessional`),
                    exportField: `${selector}physicianPetitioner.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}physicianPetitioner.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.caseNumber`),
                    exportField: `${selector}caseStudy.caseNumber`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}caseStudy.caseNumber`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.PRIORITY,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.priority`),
                    exportField: `${selector}priority.name`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}priority.level`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.pathologist`),
                    exportField: `${selector}pathologist.fullName`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}pathologist.fullName`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: hideAll
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.externalReference`),
                    exportField: `${selector}externalReference`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}externalReference`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.requestDate`),
                    exportField: `${selector}requestDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}requestDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.registrationDate`),
                    exportField: `${selector}registrationDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}registrationDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.cancellationDate`),
                    exportField: `${selector}cancellationDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}cancellationDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.returnedDate`),
                    exportField: `${selector}returnedDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}returnedDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.DATETIME,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.completedDate`),
                    exportField: `${selector}completedDate`,
                    exportType: AgGridExportType.DATETIME,
                    field: `${selector}completedDate`,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.destinationHealthManagementArea'),
                    field: `${selector}destinationHealthManagementArea.name`,
                    exportField: `${selector}destinationHealthManagementArea.name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.destinationCenter'),
                    field: `${selector}destinationDepartment.center.name`,
                    exportField: `${selector}destinationDepartment.center.name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.destinationUnit'),
                    field: `${selector}destinationDepartment.name`,
                    exportField: `${selector}destinationDepartment.name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.receiver'),
                    field: `${selector}receiver`,
                    exportField: `${selector}receiver`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.regime'),
                    field: `${selector}regime.name`,
                    exportField: `${selector}regime.name`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc('scientificAnalysis.grid.results.request.episode'),
                    field: `${selector}episode`,
                    exportField: `${selector}episode`,
                    exportType: AgGridExportType.TEXT,
                    sortable: false,
                    filter: false,
                    minWidth: 200,
                    flex: 1,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.relevantClinicData`),
                    exportField: `${selector}clinicalData`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}clinicalData`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.TEXT,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.notes`),
                    exportField: `${selector}notes`,
                    exportType: AgGridExportType.TEXT,
                    field: `${selector}notes`,
                    sortable: false,
                    filter: false,
                    flex: 1,
                    minWidth: 200,
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.hasAttachments`),
                    headerTooltip: i18n.tc(`scientificAnalysis.grid.results.request.hasAttachments`),
                    exportField: `${selector}hasAttachments`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasAttachments`,
                    sortable: false,
                    filter: false,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate(`far fa-paperclip`)
                    },
                    hide: true
                },
                {
                    type: AgGridColumnType.CHECK_ICON,
                    headerName: i18n.tc(`scientificAnalysis.grid.results.request.hasIncidents`),
                    headerTooltip: i18n.tc(`scientificAnalysis.grid.results.request.hasIncidents`),
                    exportField: `${selector}hasIncidents`,
                    exportType: AgGridExportType.BOOLEAN,
                    field: `${selector}hasIncidents`,
                    sortable: false,
                    filter: false,
                    headerComponentParams: {
                        template: buildIconHeaderTemplate(`far fa-exclamation-triangle`)
                    },
                    hide: true
                }
            ]
        }
    }

    public getGridColumns(selector: string = ''): (ColDef | ExportedColDef | ExportedColGroupDef)[] {
        return [
            { type: AgGridColumnType.CUSTOM_CHECKBOX },
            ScientificAnalysisRequestDatasource.getEntityGridColumns(),
            ScientificAnalysisPatientDatasource.getEntityGridColumns(`caseStudy.patient.`, true)
        ]
    }

    public async paginator(_filterModel: CommonFilterModel, params: CommonParams, scrollId: string, totalElements: number): Promise<InfinitePaginatorResponse<ScientificAnalysisEntityRequest>> {
        const response: InfinitePaginatorResponse<ScientificAnalysisEntityRequest> = await scientificAnalysisService.search(this.scientificAnalysis, params, scrollId, totalElements) as InfinitePaginatorResponse<ScientificAnalysisEntityRequest>
        this.setTotalElements(response.scrollPage.totalElements)
        return {
            content: response.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response?.scrollPage?.scrollId)),
                size: response.scrollPage.size,
                totalElements: response.scrollPage.totalElements
            }
        }
    }

    public getPatAndLabId(selectedRow: ScientificAnalysisEntityRequest): [number, Nullable<number>] {
        return [selectedRow.caseStudy.patient.id, selectedRow.caseStudy.laboratoryId]
    }

    public getLabAndCaseId(selectedRow: ScientificAnalysisEntityRequest): [Nullable<number>, Nullable<number>] {
        return [selectedRow.caseStudy.laboratoryId, selectedRow.caseStudy.id]
    }

    public getHTMLFieldsList(data: ScientificAnalysisEntityRequest): string[] {
        return []
    }
}
